import { useCallback, useEffect, useState } from "react";
import { Search, Trash2, Mail, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "./api";
import { PageHeader, Card, Button, EmptyState } from "./ui";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contact-messages", { params: { search, read: filter } });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [search, filter]);
  useEffect(() => { load(); }, [load]);

  const toggleRead = async (id, read) => {
    try { await api.patch(`/admin/contact-messages/${id}`, { read }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success("Silindi"); setOpen(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="İletişim Mesajları" description="Gelen iletişim formu mesajları." />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Ad, e-posta, konu, mesaj…"
            data-testid="messages-search"
            className="w-full bg-[#050505] border border-white/10 text-white placeholder:text-zinc-600 focus:border-[#FF4F00] focus:outline-none font-mono text-sm h-11 pl-10 pr-4"
          />
        </div>
        <select
          value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="messages-filter"
          className="bg-[#050505] border border-white/10 text-white font-mono text-sm h-11 px-3 focus:border-[#FF4F00] focus:outline-none"
        >
          <option value="all">Tümü</option>
          <option value="unread">Okunmamış</option>
          <option value="read">Okunmuş</option>
        </select>
        <Button variant="secondary" onClick={load}>Ara</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState message="Mesaj yok." />
      ) : (
        <div className="space-y-px bg-white/10 border border-white/10" data-testid="messages-list">
          {items.map((m) => (
            <div key={m.id} className={`bg-[#0C0C0F] p-5 flex items-start gap-4 ${!m.read ? "border-l-2 border-[#FF4F00]" : ""}`}>
              <button onClick={() => toggleRead(m.id, !m.read)} data-testid={`msg-toggle-${m.id}`} className="mt-1 text-zinc-400 hover:text-[#FF4F00]">
                {m.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4 text-[#FF4F00]" />}
              </button>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setOpen(m); if (!m.read) toggleRead(m.id, true); }}>
                <div className="flex items-center gap-3">
                  <div className="font-display font-bold">{m.name}</div>
                  <div className="text-xs text-zinc-500">{m.email}</div>
                </div>
                {m.subject && <div className="text-sm text-zinc-300 mt-1">{m.subject}</div>}
                <div className="text-xs text-zinc-500 mt-1 truncate">{m.message}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-2">{m.created_at.slice(0, 16).replace("T", " ")}</div>
              </div>
              <button onClick={() => remove(m.id)} data-testid={`msg-delete-${m.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-6" onClick={() => setOpen(null)}>
          <div className="max-w-xl w-full border border-white/10 bg-[#0C0C0F] p-8" onClick={(e) => e.stopPropagation()} data-testid="msg-detail-modal">
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-4">{open.name}</h3>
            <div className="text-xs text-zinc-400 mb-6">{open.email} {open.phone && `· ${open.phone}`}</div>
            {open.subject && <div className="font-display text-lg font-bold mb-2">{open.subject}</div>}
            <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{open.message}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-6">{open.created_at}</div>
            <Button variant="secondary" className="mt-6" onClick={() => setOpen(null)}>Kapat</Button>
          </div>
        </div>
      )}
    </div>
  );
}
